import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, filter, switchMap } from 'rxjs/operators';
import { BrandsEnum, IComponentWrapper, SdComponentWrapperComponent, SizesEnum } from '@seb/web-design-system/core';
import { BaseCrossSellComponent } from '@seb/web-common/core/components/base/base-cross-sell/base-cross-sell.component';
import { CrossSellCodeInterface, LameConfig } from '@seb/web-common/core/models';
import { OrderTypeEnum, pageTypes, ProductAccessoryTypeEnum } from '@seb/web-common/core/enums';
import { BrandCornerMapper, HeadingParagraphMapper } from '@seb/web-common/core/mappers';
import { AccessoryCrossSellService } from '@seb/web-common/core/services';
import { DynamicContentShowMore } from '@seb/web-design-system/show-more';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'wc-product-cross-sell',
  templateUrl: './product-cross-sell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SdComponentWrapperComponent, BaseCrossSellComponent]
})
export class ProductCrossSellComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  isReplacementParts = false;

  @Input()
  productCode = '';

  @Input()
  isAccessory = false;

  @Input()
  previousProductCode = 0;

  @Input()
  brand = BrandsEnum.default;

  @Input()
  pageType: pageTypes;

  @Input()
  dynamicContentShowMore: DynamicContentShowMore = {
    isActive: true,
    isLast: false
  };

  @Output()
  hasAccessories = new EventEmitter<boolean>();

  @Output()
  hasReplacementParts = new EventEmitter<boolean>();

  pageSize = 6;
  finalPageSize = 3;
  page = 0;
  totalPages;
  accessoriesSearchSubject: Subject<number>;
  lameConfig: LameConfig;
  wrapperContent: IComponentWrapper;
  codes: CrossSellCodeInterface;
  productCodeForAccessory;
  subscription: Subscription = new Subscription();
  readonly context = 'Cross-sell';

  constructor(
    private translateService: TranslateService,
    private readonly accessoryCrossSellService: AccessoryCrossSellService,
    private cdRef: ChangeDetectorRef,
    private headingParagraphMapper: HeadingParagraphMapper,
    private brandCornerMapper: BrandCornerMapper,
    @Inject(PLATFORM_ID) private platformId: any
  ) {}

  ngOnInit(): void {
    this.lameConfig = this.brandCornerMapper.toWcConfig(this.brand)?.pdp?.accessories;
    const richParagraphData = {
      heading: {
        headline: this.translateService.instant(
          this.isReplacementParts ? 'pdp.sectionTitles.replacementParts' : 'pdp.sectionTitles.accessories'),
        headlineLevel: 'H2'
      },
      titleTheme: this.lameConfig?.titleTheme,
      isUnderlined: this.lameConfig?.titleUnderlined,
      titleLevel: 2,
      size: SizesEnum.l
    };
    this.wrapperContent = {
      links: [],
      footNotes: [],
      heading: this.headingParagraphMapper.toHeadingParagraph(richParagraphData)
    };
  }

  ngOnChanges() {
    if (isPlatformBrowser(this.platformId)) {
      this.accessoriesSearchSubject = new Subject<number>();
      this.codes = {};
      this.page = 0;
      this.pageSize = 6;
      this.getProductAccessories();
      this.accessoriesSearchSubject.next(this.page);
    }
  }

  getProductAccessories() {
    const productCode = (this.isAccessory || this.isReplacementParts) && this.previousProductCode
      ? this.previousProductCode + ''
      : this.productCode;
    const orderType = this.pageType === pageTypes.CSP_ACCESSORY || this.pageType === pageTypes.PDP_ACCESSORY || this.pageType === pageTypes.CSP_IFU
      ? OrderTypeEnum.SERVICE
      : OrderTypeEnum.MARKETING;
    const accessoriesSearch$ = this.createSearchObservable([productCode], ProductAccessoryTypeEnum.ACCESSORY, orderType, [this.productCode]);
    const detachedPiecesSearch$ = this.createSearchObservable([productCode], ProductAccessoryTypeEnum.DETACHED_PIECE, orderType, [this.productCode]);

    this.productCodeForAccessory = this.pageType === pageTypes.PDP_ACCESSORY ? null : productCode;

    if (this.isReplacementParts) {
      this.handleSearchResponse(detachedPiecesSearch$, this.hasReplacementParts);
    } else if ((this.isAccessory && this.previousProductCode) || !this.isAccessory) {
      this.handleSearchResponse(accessoriesSearch$, this.hasAccessories);
    } else {
      if (this.isAccessory && this.productCode && !this.previousProductCode) {
        const accessoriesSearchSubject$ = this.accessoriesSearchSubject.asObservable().pipe(
          switchMap(() => {
            return this.accessoryCrossSellService
              .getAccessoriesCrossSell(this.productCode, this.pageSize, this.page);
          })).subscribe((response) => {
          this.mapData(response);
          this.cdRef.markForCheck();
        });
        this.subscription.add(accessoriesSearchSubject$);
      }
    }
  }

  createSearchObservable(productCode: string[], type: ProductAccessoryTypeEnum, orderType: OrderTypeEnum, excludedAccessories?: string[]): Observable<any> {
    return this.accessoriesSearchSubject.asObservable().pipe(
      switchMap((page) => {
        return this.accessoryCrossSellService.getProductAccessoriesOrderedByType(
          productCode,
          orderType,
          type,
          excludedAccessories,
          page,
          this.pageSize
        ).pipe(
          filter((p) => !!p)
        );
      })
    );
  };

  handleSearchResponse(searchObservable: Observable<any>, eventEmitter: EventEmitter<any>) {
    const searchObservable$ = searchObservable.pipe(
      debounceTime(1000)
    ).subscribe((response) => {
      this.mapData(response);
      this.subscription.add(searchObservable$);
      eventEmitter.emit(this.codes?.products?.length > 0);
    });
  }

  mapData(response) {
    if (!this.totalPages || response?.pagination.totalPages !== this.totalPages) {
      this.totalPages = Math.ceil(response?.pagination?.totalResults / this.finalPageSize);
    }
    this.codes = {
      products: [
        ...this.codes?.products || [],
        ...response.products.map((product) => product.code)
      ]
    };

    if ((this.page === 0 && this.totalPages === 1) || (this.page >= this.totalPages)) {
      this.dynamicContentShowMore = {
        isActive: false,
        isLast: true
      };
    }
  }

  onNext(): void {
    this.page = (this.page === 0) ? 2 : this.page + 1;
    this.pageSize = this.finalPageSize;
    this.loadNextPage(this.page);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  loadNextPage(page: number): void {
    if (page >= this.totalPages) {
      this.dynamicContentShowMore = {
        isActive: false,
        isLast: true
      };
      return;
    }
    this.accessoriesSearchSubject.next(page);
  }
}
